import type { VipInfo, VipLevel, WhitelistUser } from "@/types/vip";
import {
	formatVipUsdThreshold,
	VIP_GOLD_USD_FALLBACK_THRESHOLD,
	VIP_GOLD_USD_THRESHOLD,
	VIP_SILVER_USD_FALLBACK_THRESHOLD,
	VIP_SILVER_USD_THRESHOLD,
} from "./config";

export type VipLanguage = "zh" | "en";
export type VipDisplayTier = "guest" | "explorer" | "silver" | "gold";

export interface ResolvedVipStatus {
	vipInfo: VipInfo;
	displayTier: VipDisplayTier;
	isVip: boolean;
	isHiddenTrial: boolean;
}

interface ResolveVipStatusInput {
	language: VipLanguage;
	subjectId?: string;
	isAuthenticated: boolean;
	lpUsdValue: number;
	previousLevel?: VipLevel;
	whitelistInfo: WhitelistUser | null;
}

export function resolveVipStatus({
	language,
	subjectId,
	isAuthenticated,
	lpUsdValue,
	previousLevel,
	whitelistInfo,
}: ResolveVipStatusInput): ResolvedVipStatus {
	if (!subjectId && !isAuthenticated) {
		return {
			vipInfo: {
				level: 0,
				name: language === "zh" ? "未登录" : "Not Logged In",
				description: language === "zh" ? "请先登录" : "Please login first",
				isActive: false,
			},
			displayTier: "guest",
			isVip: false,
			isHiddenTrial: false,
		};
	}

	if (whitelistInfo) {
		return {
			vipInfo: {
				level: 2,
				name: language === "zh" ? "黄金会员体验" : "Gold Member Experience",
				description:
					whitelistInfo.info ||
					(language === "zh"
						? "在有效期内享有黄金会员权限"
						: "Gold member access during the valid period"),
				expiresAt: parseVipDate(whitelistInfo.end_time),
				isActive: true,
			},
			displayTier: "gold",
			isVip: true,
			isHiddenTrial: true,
		};
	}

	const qualifiesForGold =
		lpUsdValue >= VIP_GOLD_USD_THRESHOLD ||
		(previousLevel === 3 && lpUsdValue >= VIP_GOLD_USD_FALLBACK_THRESHOLD);
	if (qualifiesForGold) {
		return {
			vipInfo: {
				level: 3,
				name: language === "zh" ? "黄金会员" : "Gold Member",
				description:
					language === "zh"
						? `BabyOdin LP 总价值达到 ${formatVipUsdThreshold(VIP_GOLD_USD_THRESHOLD)}`
						: `BabyOdin LP value reaches ${formatVipUsdThreshold(VIP_GOLD_USD_THRESHOLD)}`,
				isActive: true,
			},
			displayTier: "gold",
			isVip: true,
			isHiddenTrial: false,
		};
	}

	const qualifiesForSilver =
		lpUsdValue >= VIP_SILVER_USD_THRESHOLD ||
		(previousLevel === 1 && lpUsdValue >= VIP_SILVER_USD_FALLBACK_THRESHOLD);
	if (qualifiesForSilver) {
		return {
			vipInfo: {
				level: 1,
				name: language === "zh" ? "白银会员" : "Silver Member",
				description:
					language === "zh"
						? `BabyOdin LP 总价值达到 ${formatVipUsdThreshold(VIP_SILVER_USD_THRESHOLD)}`
						: `BabyOdin LP value reaches ${formatVipUsdThreshold(VIP_SILVER_USD_THRESHOLD)}`,
				isActive: true,
			},
			displayTier: "silver",
			isVip: true,
			isHiddenTrial: false,
		};
	}

	return {
		vipInfo: {
			level: 0,
			name: language === "zh" ? "探索者" : "Explorer",
			description:
				language === "zh"
					? "当前未满足白银会员门槛，享有探索者权限"
					: "Silver value threshold not met, Explorer access applies",
			isActive: false,
		},
		displayTier: "explorer",
		isVip: false,
		isHiddenTrial: false,
	};
}

export function parseVipDate(dateStr: string): Date {
	const year = parseInt(dateStr.substring(0, 4), 10);
	const month = parseInt(dateStr.substring(4, 6), 10) - 1;
	const day = parseInt(dateStr.substring(6, 8), 10);
	const date = new Date(year, month, day);
	date.setHours(23, 59, 59, 999);
	return date;
}

export function getVipLevelName(level: VipLevel, language: VipLanguage): string {
	switch (level) {
		case 1:
			return language === "zh" ? "白银会员" : "Silver Member";
		case 2:
			return language === "zh" ? "黄金会员体验" : "Gold Member Experience";
		case 3:
			return language === "zh" ? "黄金会员" : "Gold Member";
		default:
			return language === "zh" ? "探索者" : "Explorer";
	}
}
